/*
 * Copyright (c) 2024. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import { windowEvents } from '@/util/interaction'
import {
    Abort,
    createDisposer,
    debounce,
    type DebounceCallback,
    type Disposable,
    EventHostImpl,
    getSearchParams,
    globalRef,
    logDebug,
    logVerbose,
    LruCache,
    type Maybe,
    type MaybePromise,
    type MemoizedFunction,
    noop,
    type ReadonlyEventHost,
    ReportableError,
    type Unsubscribe,
    ValidationError,
    withAbort,
} from '@amazon/vinyl-util'
import { claimErrorSurface, createErrorHandler } from '@/errorHandler'
import { startViewTransition } from '@/transition/viewTransition'
import { getAppScroller, scrollToTop } from '@/util/domUtil'
import { doWhenIdle, IdlePriority } from '@/util/idleQueue'

type RouteParams = Record<string, string>
export type RouteViewFactory<T extends RouteParams> = (
    params: T,
    path: string
) => MaybePromise<Element | null>

const ROUTE_CACHE_CAPACITY = 3

/**
 * A route's views memoized by path, so routing back shows the live page. A path
 * whose factory renders nothing (a redirect) is not remembered: its effect has to
 * run on every visit, not just the first.
 */
function memoizeViews(
    factory: RouteViewFactory<RouteParams>,
    capacity: number
): MemoizedFunction & RouteViewFactory<RouteParams> {
    const views = new LruCache<string, Promise<Element | null>>(capacity)
    const memoized = (params: RouteParams, path: string): Promise<Element | null> => {
        const cached = views.get(path)
        if (cached) return cached
        const view = (async () => (await factory(params, path)) ?? null)()
        views.set(path, view)
        view.then((element) => {
            if (element === null && views.get(path) === view) views.delete(path)
        }, noop)
        return view
    }
    return Object.assign(memoized, { clear: () => views.clear() })
}

export type PropsFromKeys<Keys extends readonly string[]> = {
    readonly [K in Keys[number]]: string
}

const trailingSlash = /\/+$/

// Elements the user is actively typing into — moving focus to the new page's
// heading would eject them mid-keystroke (e.g. a route's autofocused search
// box).
function isEditableElement(element: Element | null): boolean {
    if (!element) return false
    const tagName = element.tagName
    return (
        tagName === 'INPUT' ||
        tagName === 'TEXTAREA' ||
        tagName === 'SELECT' ||
        (element as HTMLElement).isContentEditable
    )
}

// The mounted page's own primary heading — an `<h1>`, or an explicit
// `role="heading"` for a page that marks a non-`h1` element as its heading.
const HEADING_SELECTOR = 'h1, [role="heading"]'

/**
 * Named aliases for per-param regex constraints so routes can write
 * `:showId(UUID)` instead of inlining a 36-char pattern. The alias
 * value must contain NO capturing groups — the matcher aligns
 * `paramNames` to capture groups by index, so an extra `(...)` inside a
 * constraint would shift every subsequent param. Anything not in this
 * map is treated as a raw regex fragment (also must be capture-free).
 */
const PARAM_CONSTRAINT_ALIASES: Record<string, string> = {
    UUID: '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}',
}

export interface RouterOptions {
    /**
     * Use hashbang URLs (#!/path) instead of clean URLs (/path)
     * Default: false
     */
    readonly useHashbang?: boolean

    /**
     * Seconds to debounce eager `preloadRoute` (link hover / touch / focus).
     * Leading + trailing: the first hover of a burst warms immediately, then
     * a burst collapses into one trailing warm of the path the pointer ended
     * on — so quickly sweeping past links doesn't spam route builds.
     * Default: 0.2s.
     */
    readonly preloadDebounceSeconds?: number
}

export const defaultRouterOptions: Required<RouterOptions> = {
    useHashbang: false,
    preloadDebounceSeconds: 0.2,
}

/**
 * `KeysList` is the tuple of each route's `paramNames` tuple. `configure`
 * infers it from the literal route config so each route's `factory` receives
 * a `params` object typed with EXACTLY the param names that route declared —
 * `params.wrongName` is a compile error. Do not widen this to
 * `RouteOptions<any>[]`; that erases the inference.
 */
export interface RouterConfigureOptions<
    KeysList extends readonly (readonly string[])[] = readonly (readonly string[])[],
> {
    /**
     * The view upon which to add created views.
     */
    readonly stage: HTMLElement

    readonly routes: { readonly [I in keyof KeysList]: RouteOptions<KeysList[I]> }

    readonly fallback?: () => Element

    /**
     * Rendered onto the stage when a matched route's factory throws / rejects
     * (a transition or render failure) instead of leaving a blank stage. A
     * superseded navigation (its `setStage` aborted) never paints it. Omitted:
     * a failure clears the stage as before. Receives the caught error so the
     * fallback can tailor its copy/CTAs to the failure kind.
     */
    readonly errorFallback?: (error?: unknown) => Element

    /**
     * Gate for eager/idle preload (link hover / focus, boot warm). When it
     * returns false, `preloadRoute` builds nothing — navigation-time loads are
     * unaffected. The app wires this to the auth state so a signed-out session
     * never pre-fires auth-required route data (which 401s) for a route the
     * user hasn't navigated to. Omitted: preload is always allowed.
     */
    readonly canPreload?: () => boolean
}

export interface RouteOptions<Keys extends readonly string[]> {
    readonly pattern: string
    readonly paramNames: Keys
    readonly factory: RouteViewFactory<PropsFromKeys<Keys>>

    /**
     * The number of elements to cache based on the path.
     * Default: ROUTE_CACHE_CAPACITY
     */
    readonly cacheCapacity?: number

    /**
     * Allow `preloadRoute(path)` to build this route's view ahead of
     * navigation (link hover / focus, boot warm). Only safe for side-effect-
     * free page factories: a factory that redirects (`navigateTo`), opens an
     * overlay (chat), or fires playback must NOT set this, or those effects
     * would fire on hover.
     */
    readonly allowPreload?: boolean
}

interface Route {
    readonly pattern: string
    readonly regex: RegExp
    readonly paramNames: readonly string[]
    readonly factory: MemoizedFunction & RouteViewFactory<any>
    readonly preload: boolean
}

export interface NavigatingEvent {
    readonly previous: string | null
    readonly current: string
    readonly defaultPrevented: boolean

    preventDefault(): void
}

class NavigatingEventImpl implements NavigatingEvent {
    defaultPrevented: boolean = false

    constructor(
        readonly previous: string | null,
        readonly current: string
    ) {}

    preventDefault(): void {
        this.defaultPrevented = true
    }
}

export interface NavigateEvent {
    readonly previous: string | null
    readonly current: string
}

export interface PageShownEvent {
    readonly path: string
    /** The matched route's `pattern`, or null for the fallback page. */
    readonly pattern: string | null
    readonly params: Readonly<RouteParams>
    readonly view: Element
}

export interface RouterEventMap {
    /**
     * Dispatched before a navigation.
     * May call preventDefault to prevent the navigation.
     */
    readonly navigating: NavigatingEvent

    /**
     * Dispatched when a navigation has completed.
     * See `navigating` for before a navigate has completed.
     */
    readonly navigate: NavigateEvent

    /**
     * Dispatched on EVERY `navigateTo` call — including same-path
     * "navigations" that would otherwise short-circuit. Useful for
     * side-effects (e.g. "enter PiP whenever the user attempts to
     * leave the video stage, even if they're targeting the page
     * they're already on") that shouldn't depend on the path
     * actually changing.
     */
    readonly navigateAttempt: NavigateEvent

    /**
     * Dispatched once a navigation's page is on the stage — a cached view shown
     * again included; never for a redirect, a superseded navigation or the error page.
     */
    readonly pageShown: PageShownEvent
}

/** How a `navigateTo` call should treat history + scroll. */
export interface NavigateOptions {
    /**
     * Push a new history entry for this navigation. Defaults to true;
     * the popstate path uses false so the URL doesn't end up
     * double-tagged.
     */
    readonly pushHistory?: boolean
    /**
     * Rewrite the CURRENT history entry's URL to `path` instead of
     * pushing a new one. Takes precedence over `pushHistory`. Unlike the
     * popstate path (`pushHistory: false`, which leaves the URL alone),
     * `replace` rewrites the URL AND preserves the current depth tag —
     * so a redirect route (`/`→`/feed`) can route through the router,
     * keeping `currentPath` in sync with the URL and the `bullfrogDepth`
     * tag intact, without adding the redirect source to the back stack.
     */
    readonly replace?: boolean
    /**
     * Where to leave the page's scroll position after the route loads:
     *   - `top` — scroll to top (the default for fresh forward navigations)
     *   - `restore` — restore the scroll position last seen on this
     *     path (the default for popstate / back navigation)
     *   - `keep` — leave whatever the route restored on its own
     *
     * The home-button uses `restore` on the first hit; a second click
     * to the same path scrolls to top instead of no-op'ing — see
     * the "same path" branch in `RouterImpl.navigateTo`.
     */
    readonly scroll?: 'top' | 'restore' | 'keep'
}

export interface Router extends ReadonlyEventHost<RouterEventMap> {
    /**
     * Sets route configurations.
     */
    configure<const KeysList extends readonly (readonly string[])[]>(
        options: RouterConfigureOptions<KeysList>
    ): void

    /**
     * Clears all set routes.
     */
    clearRoutes(): void

    /**
     * Clears cached routes and reloads the current route.
     */
    hardReload(): void

    /**
     * Navigates to the given path.
     */
    navigateTo(path: string, options?: NavigateOptions | boolean): void

    /** Whether `navigateTo(path)` would stay on the page already shown. */
    isShownPath(path: string): boolean

    /**
     * Build a route's view ahead of navigation, using the same resolution +
     * memoized factory `navigateTo` uses — minus the history push and the
     * stage swap. The built element lands in the route's memoize cache keyed
     * by path, so a later `navigateTo(path)` reuses it instead of rebuilding.
     * Only routes flagged `preload` are eligible; an unflagged or unknown path
     * is a safe no-op. `idle` defers the build to the idle queue at LOW
     * priority (boot warm); the default is eager (link hover / focus).
     *
     * The single entry point for every hover/press warmer: a null / external /
     * non-`/` href is a no-op HERE, so callers (the `Link` component, chat
     * markdown links) hand any anchor href straight through without a
     * pre-check — only same-origin absolute paths preload.
     */
    preloadRoute(path: Maybe<string>, opts?: { readonly idle?: boolean }): void

    /**
     * Rewrite the URL of the CURRENTLY-mounted stage WITHOUT re-rendering
     * it. For a page that owns a mutable sub-segment of its own URL — e.g.
     * the chat session id in `/chat/:sessionId`, which changes as the user
     * starts / switches threads while the ChatPage element stays mounted.
     * Going through `navigateTo` would match a different path, remount the
     * page, and re-run its `onConnect`; this keeps the element in place and
     * only moves the address bar + history entry.
     *
     * `push` adds a back-stack entry (so Back returns to the prior
     * sub-resource); otherwise the current entry is rewritten in place.
     * Either way `currentPath` and the `bullfrogDepth` tag stay in sync so
     * `back()` and popstate resolve correctly.
     */
    setCurrentUrl(path: string, options?: { readonly push?: boolean }): void

    /**
     * Go back in history or navigate to home if no history exists.
     */
    back(): void
}

export class RouterImpl extends EventHostImpl<RouterEventMap> implements Router, Disposable {
    get [Symbol.toStringTag](): string {
        return 'RouterImpl'
    }

    private routes: readonly Route[] | null = null
    private fallback: (() => Element) | null = null
    private errorFallback: ((error?: unknown) => Element) | null = null
    private canPreload: (() => boolean) | null = null
    private readonly errorHandler = createErrorHandler(this)

    private setStageAbort = new Abort()
    private readonly disposer = createDisposer()
    private currentPath: string | null = null
    /**
     * The path last requested through `navigateTo` for the currently
     * mounted page — its canonical entry, BEFORE any in-place URL rewrite
     * the page performs on itself via `setCurrentUrl`.
     *
     * A page that owns a mutable sub-segment of its own URL (chat's
     * `/chat/:sessionId`) mounts on the bare path (`/chat`) and then
     * rewrites `currentPath` to `/chat/<id>` WITHOUT remounting. Re-clicking
     * the same nav link fires `navigateTo('/chat')` again, which no longer
     * equals the rewritten `currentPath` — so we also compare against the
     * base we mounted from. Without it the repeat click pushes a duplicate
     * history entry and the user has to press Back once per click.
     */
    private currentBasePath: string | null = null
    private readonly useHashbang: boolean
    /**
     * Count of in-app history entries we've pushed since the page loaded —
     * used to decide whether `back()` should go through browser history or
     * fall back to home. Reaching 0 means there's nothing for us to go back
     * to within the app on this session (e.g. user reloaded a deep link).
     */
    private inAppHistoryDepth = 0

    private view!: HTMLElement

    // Whether an eager-preload debounce window is currently open. The first
    // hover opens it (leading warm); it closes on the trailing edge.
    private preloadWindowOpen = false
    // Latest path hovered while the window is open; the trailing edge warms
    // this one so a burst ends on the target the pointer actually settled on.
    private pendingPreloadPath: string | null = null
    private readonly scheduleTrailingPreload: DebounceCallback
    private readonly options: Required<RouterOptions>

    constructor(options?: RouterOptions) {
        super()
        this.options = { ...defaultRouterOptions, ...options }

        this.useHashbang = options?.useHashbang ?? false
        const { add } = this.disposer
        this.scheduleTrailingPreload = add(
            debounce(this.preloadTrailingPath, this.options.preloadDebounceSeconds)
        )

        const handleStateChange = () => {
            const path = this.getWindowPath()
            this.navigateTo(path, false)
        }
        add(windowEvents.on('popstate', handleStateChange))
        history.scrollRestoration = 'manual'
    }

    configure<const KeysList extends readonly (readonly string[])[]>(
        options: RouterConfigureOptions<KeysList>
    ): void {
        this.currentPath = null
        this.currentBasePath = null
        this.view = options.stage
        this.fallback = options.fallback ?? null
        this.errorFallback = options.errorFallback ?? null
        this.canPreload = options.canPreload ?? null

        const routes: Route[] = []
        for (const routeOptions of options.routes as readonly RouteOptions<readonly string[]>[]) {
            const { paramNames, pattern, cacheCapacity, factory, allowPreload } = routeOptions
            paramNames.forEach((paramName) => {
                if (!pattern.includes(`:${paramName}`))
                    throw new ValidationError(`missing :${paramName} in route`)
            })

            // Param syntax: `/:name`, optionally `(constraint)` and/or a
            // trailing `?` for an optional segment — e.g.
            // `/:showId(UUID)`, `/:slug?`, `/:id(UUID)?`. The constraint
            // is an alias from PARAM_CONSTRAINT_ALIASES (e.g. `UUID`) or a
            // raw capture-free regex fragment. Without a constraint a
            // param stays unconstrained (`[^/]+`).
            const regexPattern = pattern
                .replace(/\/+$/, '') // remove trailing slashes
                .replace(
                    /\/:([a-zA-Z0-9_]+)(?:\(([^)]+)\))?(\?)?/g,
                    (
                        _full,
                        _name: string,
                        constraint: string | undefined,
                        optional: string | undefined
                    ) => {
                        const inner = !constraint
                            ? '[^/]+'
                            : constraint in PARAM_CONSTRAINT_ALIASES
                              ? PARAM_CONSTRAINT_ALIASES[constraint]
                              : constraint
                        return `(?:/(${inner}))${optional ?? ''}`
                    }
                )
            logVerbose(this, 'registered route:', regexPattern)

            const regex = new RegExp(`^${regexPattern}/?$`)

            const route: Route = {
                pattern,
                regex,
                paramNames,
                factory: memoizeViews(factory, cacheCapacity ?? ROUTE_CACHE_CAPACITY),
                preload: allowPreload ?? false,
            }
            routes.push(route)
        }
        this.routes = routes
        // Initial navigation: don't push a new entry — the URL bar already
        // matches. Also seed inAppHistoryDepth from any tag we left on the
        // current entry in a previous session (browsers preserve history.state
        // across reloads), so the user's existing in-app stack stays intact.
        const tagged = (history.state as { bullfrogDepth?: number } | null)?.bullfrogDepth
        this.inAppHistoryDepth = typeof tagged === 'number' ? tagged : 0
        this.navigateTo(this.getWindowPath(), false)
    }

    clearRoutes() {
        this.clear()
        this.clearRouteCaches()
        this.routes = null
    }

    private hasNavigated = false

    // Scroll positions are keyed by the router's internal currentPath rather
    // than window.location.href because popstate fires AFTER the browser has
    // already changed the URL — so by the time `saveScrollPosition` runs,
    // window.location is the destination, not the page being left.
    private readonly scrollPositions = new LruCache<string, number>(20)

    private saveScrollPositionFor(path: string | null) {
        if (path == null) return
        this.scrollPositions.set(path, getAppScroller()?.scrollTop ?? 0)
    }

    private restoreScrollPositionFor(path: string | null) {
        if (path == null) return
        const y = this.scrollPositions.get(path) ?? 0
        // Suppress scroll-anchoring while the restored page settles.
        // Without this, a late layout event inside the newly-mounted
        // route (image decode finishing, hero palette re-tint,
        // shoveler shimmer swap) shifts the scroll by tens of pixels
        // after our scrollTo runs — the browser latches onto a
        // sub-viewport element and drags the scroll to keep it
        // "visible", overriding the position we just wrote. Turning
        // overflow-anchor off tells the browser: WE own the scroll
        // now, don't second-guess. Cleared after a couple of frames
        // once layout has settled. Applied to the page scroller
        // (`#main-content`), the element that actually scrolls.
        const scroller = getAppScroller()
        if (!scroller) return
        const previousAnchor = scroller.style.overflowAnchor
        scroller.style.overflowAnchor = 'none'
        scroller.scrollTo({ top: y, behavior: 'auto' })
        // Re-pin once on the next frame: if any code inside the
        // route's onConnect emitted a scrollTo/scrollBy of its own
        // during the same tick, we're still the authoritative source
        // for back-nav intent.
        requestAnimationFrame(() => {
            if (scroller.scrollTop !== y) scroller.scrollTo({ top: y, behavior: 'auto' })
            requestAnimationFrame(() => {
                scroller.style.overflowAnchor = previousAnchor
            })
        })
    }

    private getWindowPath() {
        if (this.useHashbang) {
            // Extract path from hashbang URL (#!/path)
            const hash = window.location.hash
            if (hash.startsWith('#!')) {
                return hash.substring(2) || '/'
            }
            return '/'
        }
        return window.location.pathname || '/'
    }

    isShownPath(path: string): boolean {
        return this.currentPath === path || this.currentBasePath === path
    }

    navigateTo(path: string, options: NavigateOptions | boolean = true) {
        // Legacy boolean signature: `navigateTo(path, pushHistory)`. Map onto
        // the options object so call sites can migrate gradually.
        const opts: NavigateOptions =
            typeof options === 'boolean' ? { pushHistory: options } : options
        const replace = opts.replace ?? false
        const pushHistory = !replace && (opts.pushHistory ?? true)
        const scrollMode = opts.scroll ?? (pushHistory ? 'top' : 'restore')

        if (!path.startsWith('/')) path = '/' + path
        // Split off the optional query so pathname-driven equality and
        // route matching don't care about it. The full path (pathname +
        // ?query) is what we push into history so links round-trip.
        const queryIdx = path.indexOf('?')
        const pathname = queryIdx >= 0 ? path.slice(0, queryIdx) : path
        const searchStr = queryIdx >= 0 ? path.slice(queryIdx) : ''
        // Always announce the attempt, even if it's a same-path click —
        // listeners that need to react to user-intent (e.g. "enter PiP
        // when they try to leave the video page") shouldn't be blind
        // to clicks targeting the current path.
        this.dispatch('navigateAttempt', {
            previous: this.currentPath,
            current: pathname,
        })
        if (this.isShownPath(pathname)) {
            // Same-path click (query-only diff counts as same-path so
            // typing into the /search input doesn't re-mount the page).
            // Also matches the base path the current page mounted from
            // before it rewrote its own URL (chat: nav link targets bare
            // `/chat`, page rewrote `currentPath` to `/chat/<id>`) so the
            // repeat click no-ops instead of stacking a duplicate entry.
            // The home button asks for `restore` so that on first hit
            // it restores the user's last scroll position; on a second
            // hit (already on the path) we instead jump to the top —
            // that gives Home its familiar "tap to go up" behavior.
            // `keep` really means do-nothing; `top` always scrolls to
            // top.
            if (scrollMode === 'top' || scrollMode === 'restore') {
                logDebug(this, 'navigateTo same path, scrollToTop:', pathname)
                scrollToTop()
            } else {
                logDebug(this, 'navigateTo no-op:', pathname)
            }
            return
        }
        const previousPath = this.currentPath
        const event = new NavigatingEventImpl(previousPath, pathname)
        this.dispatch('navigating', event)
        if (event.defaultPrevented) {
            logDebug(this, 'navigateTo prevented', pathname)
            return
        }

        logDebug(this, 'navigateTo', pathname, pushHistory, scrollMode)
        // Save the scroll position of the page we're leaving (keyed by its
        // router path) so the back button can restore it later.
        this.saveScrollPositionFor(previousPath)

        const isFirst = !this.hasNavigated
        this.hasNavigated = true
        // Refresh the page-load anchor on each real client-side navigation so
        // per-page above-the-fold timing measures from this navigation's start.
        // The cold load keeps index.html's earlier stamp, which includes boot.
        if (!isFirst && typeof window !== 'undefined') window.pageStartedAt = Date.now()
        this.currentPath = pathname
        // Remember the path we mounted from so a later same-target click
        // still no-ops after the page rewrites its own URL (see
        // `currentBasePath`). `setCurrentUrl` deliberately leaves this alone.
        this.currentBasePath = pathname

        this.dispatch('navigate', {
            previous: previousPath,
            current: this.currentPath,
        })
        if (pushHistory || replace) {
            const url = new URL(window.location.href)
            if (this.useHashbang) {
                // For hashbang URLs, we only change the hash part
                url.hash = '!' + pathname + searchStr
            } else {
                url.pathname = pathname
                url.search = searchStr
            }
            const newPath = url.toString()
            if (replace) {
                // Rewrite the current entry's URL in place. Its in-app depth
                // is unchanged (we're still on the same history entry), so
                // re-tag with the current depth rather than bumping it —
                // this keeps `bullfrogDepth` intact where a raw
                // `replaceState(null, …)` would have wiped it.
                history.replaceState({ bullfrogDepth: this.inAppHistoryDepth }, '', newPath)
            } else {
                this.inAppHistoryDepth++
                // Tag the entry with the depth at the moment we pushed it. On
                // popstate we read state.bullfrogDepth to derive how deep we are
                // without trying to detect direction (back vs forward).
                history.pushState({ bullfrogDepth: this.inAppHistoryDepth }, '', newPath)
            }
        } else {
            // popstate path: trust the entry's tag if we put one there; if we
            // landed on an entry we never tagged (initial entry, cross-origin
            // bounce-back), treat depth as 0 — there's nothing in-app to go
            // back to.
            const tagged = (history.state as { bullfrogDepth?: number } | null)?.bullfrogDepth
            this.inAppHistoryDepth = typeof tagged === 'number' ? tagged : 0
        }
        const doTransition = async () => {
            await this.refreshStage(pathname)
            switch (scrollMode) {
                case 'top':
                    logDebug(this, 'scroll: page→top (navigated to', pathname + ')')
                    scrollToTop()
                    break
                case 'restore':
                    logDebug(this, 'scroll: page→restore (back/forward to', pathname + ')')
                    this.restoreScrollPositionFor(pathname)
                    break
                case 'keep':
                    break
            }
            if (!isFirst) this.focusPageHeading()
        }
        if (isFirst) doTransition().catch(this.errorHandler)
        else startViewTransition(doTransition).catch(this.errorHandler)
    }

    preloadRoute(path: Maybe<string>, opts?: { readonly idle?: boolean }): void {
        // Shared guard for every warmer: only same-origin absolute paths are
        // preloadable, so a null / external / non-`/` href no-ops here.
        if (path == null || !path.startsWith('/')) return
        logDebug(this, `preloadRoute path=${path}`, opts)
        const queryIdx = path.indexOf('?')
        const pathname = queryIdx >= 0 ? path.slice(0, queryIdx) : path
        if (opts?.idle) {
            // Boot warm is a fixed one-shot list, so it skips the hover
            // debounce and defers straight to the idle queue at LOW priority.
            doWhenIdle(() => this.buildPreload(pathname), IdlePriority.LOW).catch(noop)
            return
        }
        // Eager (link hover / touch / focus), debounced leading + trailing.
        if (this.preloadWindowOpen) {
            // Within an open window: remember the latest path so the trailing
            // edge warms the target the pointer actually settled on.
            this.pendingPreloadPath = pathname
        } else {
            // Leading edge: warm immediately and open the window. A lone hover
            // leaves nothing pending, so the trailing edge is a no-op.
            this.preloadWindowOpen = true
            this.buildPreload(pathname)?.catch(noop)
        }
        this.scheduleTrailingPreload()
    }

    // Executes building the pending preload path.
    private preloadTrailingPath = () => {
        const pathname = this.pendingPreloadPath
        this.pendingPreloadPath = null
        this.preloadWindowOpen = false
        if (pathname != null) this.buildPreload(pathname)?.catch(noop)
    }

    // Resolve + build lazily: the boot warm enqueues before routes are
    // configured, so match inside the task rather than at call time.
    private buildPreload(pathname: string): Promise<Element | null> | undefined {
        // Skip pre-firing auth-required route data before a session is
        // established (see `canPreload`); navigation-time loads bypass this.
        if (this.canPreload && !this.canPreload()) return
        const matched = this.matchRoute(pathname)
        if (!matched?.route.preload) return
        return Promise.resolve(matched.route.factory(matched.params, pathname))
    }

    setCurrentUrl(path: string, options?: { readonly push?: boolean }): void {
        if (!path.startsWith('/')) path = '/' + path
        const push = options?.push ?? false
        const queryIdx = path.indexOf('?')
        const pathname = queryIdx >= 0 ? path.slice(0, queryIdx) : path
        const searchStr = queryIdx >= 0 ? path.slice(queryIdx) : ''

        // Keep the router's notion of the current path aligned with the URL
        // so scroll bookkeeping and a later same-path `navigateTo` compare
        // against the right value.
        this.currentPath = pathname

        const url = new URL(window.location.href)
        if (this.useHashbang) {
            url.hash = '!' + pathname + searchStr
        } else {
            url.pathname = pathname
            url.search = searchStr
        }
        const newPath = url.toString()
        if (push) {
            this.inAppHistoryDepth++
            history.pushState({ bullfrogDepth: this.inAppHistoryDepth }, '', newPath)
        } else {
            history.replaceState({ bullfrogDepth: this.inAppHistoryDepth }, '', newPath)
        }
    }

    private clear() {
        logDebug(this, 'clear')
        this.setStage(null).catch(this.errorHandler)
        this.currentPath = null
        this.currentBasePath = null
    }

    private clearRouteCaches() {
        this.routes?.forEach((route) => route.factory.clear())
    }

    // Screen-reader focus follows scroll-restore to the new page instead of
    // staying on the control that triggered the navigation. Fires only after
    // the new page is mounted (see `doTransition`), never on the cold boot
    // navigation (the browser already announces the initial page).
    private focusPageHeading(): void {
        if (isEditableElement(document.activeElement)) return
        // An open overlay (modal / full-screen menu) owns focus; don't fight it.
        if (document.querySelector('[aria-modal="true"]')) return

        const target = this.view.querySelector<HTMLElement>(HEADING_SELECTOR) ?? getAppScroller()
        if (!target) return
        if (!target.hasAttribute('tabindex')) target.tabIndex = -1
        target.focus({ preventScroll: true })
    }

    private matchRoute(path: string): { route: Route; params: RouteParams } | null {
        if (!this.routes) return null
        const normalizedPath = path !== '/' ? path.replace(trailingSlash, '') : path
        logDebug(this, 'normalizedPath:', normalizedPath)
        for (const route of this.routes) {
            const match = normalizedPath.match(route.regex)
            if (match) {
                const params: RouteParams = {}
                route.paramNames.forEach((name, i) => {
                    const value = match[i + 1] as string | undefined
                    if (value != null) {
                        params[name] = decodeURIComponent(value)
                    }
                })
                return { route, params }
            }
        }
        return null
    }

    private async refreshStage(path: string): Promise<void> {
        // `path` is the pathname this navigation resolved to, captured in the
        // caller's closure — NOT re-read from `this.currentPath`, which a
        // newer, unrelated navigation may have already overwritten by the
        // time this (possibly transition-deferred) callback runs. Guarding
        // on `this.routes` still matters: `clearRoutes` can null it out from
        // under an in-flight navigation, and that should quietly no-op
        // rather than render the fallback / throw `MissingRouteError`.
        if (!this.routes) return
        const matched = this.matchRoute(path)
        if (!matched) {
            if (this.fallback) {
                const view = await this.setStage(() => this.fallback!())
                if (view) this.dispatch('pageShown', { path, pattern: null, params: {}, view })
                return
            }
            throw new MissingRouteError(path)
        }

        const { route, params } = matched
        const view = await this.setStage(() => route.factory(params, path))
        if (view) this.dispatch('pageShown', { path, pattern: route.pattern, params, view })
    }

    /**
     * Sets the stage view.
     *
     * @param viewFactory A factory to produce a view element to be set ont he stage. This may be a promise.
     * @return The view now on the stage, or null when none was set.
     */
    private async setStage(
        viewFactory: Maybe<() => MaybePromise<Element | null>>
    ): Promise<Element | null> {
        this.setStageAbort.abort()
        this.setStageAbort = new Abort()
        const abort = this.setStageAbort

        const stage = this.view
        stage.innerHTML = ''
        if (!viewFactory) return null

        try {
            const view = await withAbort(Promise.resolve(viewFactory()), abort)
            if (view) stage.appendChild(view)
            return view
        } catch (error) {
            // A newer navigation aborted this render — it owns the stage now,
            // so don't paint an error over it (the AbortError is silent too).
            if (abort.aborted()) return null
            // The error page below is this failure's message to the user, so
            // claim it before reporting and nothing else repeats it.
            if (this.errorFallback) claimErrorSurface(error)
            this.errorHandler(error)
            // Recover the blank stage with the error surface, tailored to the
            // failure kind. No `errorFallback` wired: fall back to the prior blank.
            if (this.errorFallback) stage.replaceChildren(this.errorFallback(error))
            return null
        }
    }

    /**
     * Clears cached routes and reloads the window path.
     */
    hardReload() {
        logDebug(this, 'hardReload')
        this.clear()
        this.clearRouteCaches()
        // Don't push a new entry — the URL bar already matches. A push here
        // would inflate the in-app history depth and force the user to back
        // through phantom entries.
        this.navigateTo(this.getWindowPath(), false)
    }

    back() {
        // If we have an in-app history entry to pop, do it. Otherwise the
        // user landed here on a fresh load (deep link / reload) and there's
        // nothing within the app to step back through — go home.
        if (this.inAppHistoryDepth > 0) {
            history.back()
        } else if (this.currentPath !== '/') {
            this.navigateTo('/')
        }
    }

    dispose() {
        this.setStage(null).catch(this.errorHandler)
        this.clearRoutes()
        this.disposer.dispose()
    }
}

// Typed to the `Router` interface (not the concrete `RouterImpl`) so a test
// can install a typed mock through `routerRef.set()` without satisfying every
// internal `RouterImpl` member.
export const routerRef = globalRef<Router>(
    () =>
        new RouterImpl({
            useHashbang: getSearchParams().has('useHashbangRoutes'),
        })
)

export function getRouter(): Router {
    return routerRef.value
}

export function navigateTo(path: string, options?: NavigateOptions): void {
    getRouter().navigateTo(path, options)
}

export function isShownPath(path: string): boolean {
    return getRouter().isShownPath(path)
}

export function setCurrentUrl(path: string, options?: { readonly push?: boolean }): void {
    getRouter().setCurrentUrl(path, options)
}

export function onNavigate(handler: (event: NavigateEvent) => void): Unsubscribe {
    return getRouter().on('navigate', handler)
}

export class MissingRouteError extends ReportableError {
    constructor(readonly path: string) {
        super(`No route registered for path: ${path}`)
        // ReportableError's constructor re-points the prototype to its own, so
        // restore ours or `instanceof MissingRouteError` is false (see
        // UnauthorizedError, InitFailedError, UploadImageError).
        Object.setPrototypeOf(this, MissingRouteError.prototype)
    }
}

export function back() {
    getRouter().back()
}

export function preloadRoute(path: Maybe<string>, opts?: { readonly idle?: boolean }): void {
    getRouter().preloadRoute(path, opts)
}
